import { Controller } from "@hotwired/stimulus"

// Turbo can connect this controller more than once for a single visit (cache
// preview, then restore). The cooldown keeps that from firing a burst.
const WARM_UP_COOLDOWN_MS = 60_000
let warmedAt = null

// Upstream statuses that can plausibly mean "the gateway Lambda was still
// waking up". 504 is deliberately absent: in this API it is deterministic —
// the origin cutting a heavy request at its ~29 s ceiling — and it repeats
// on every retry, so the "run it again" hint would be a lie. The measured
// cause lives in the SandboxService header; the numbers, in
// PANADATA/pd-api-frontend#208.
const COLD_START_UPSTREAM_STATUSES = [502, 503]
// Refusals the proxy explains with a next step (SandboxErrorMessage).
const BILLING_REFUSAL_CODES = ["insufficient_credits", "spending_limit_exceeded", "free_tier_limit", "key_expired"]

const LEDGER_MAX_ROWS = 50
const PAGE_LIMIT = 10

// The v5 sandbox console: one query bar (auto-detects nombre / RUC / typed
// id), compact results, then an entity workspace whose surface chips each
// fire one real, paginated v5 call. Every call prints a row on the ledger.
export default class extends Controller {
  static targets = [
    "query", "runButton", "routePreview", "results", "workspace",
    "entityHeader", "response", "ledger", "ledgerList", "ledgerCount", "ledgerTotal"
  ]

  static values = {
    searchUrl: String,
    costsUrl: String,
    entityUrlTemplate: String,
    surfaceUrlTemplate: String,
    apiBaseUrl: String,
    hasKey: Boolean,
    freeTier: Boolean,
    pricingUrl: String,
    confirmed: Boolean
  }

  connect() {
    this.entity = null
    this.surfaceOffsets = {}
    this.loadedPages = new Map()
    this.costs = null
    this.costsLoading = false
    this.inFlight = false
    this.ledgerRows = 0
    this.sessionCost = 0
    this.warmUp()
    // Prices are needed from the very first ledger row (a search bills the
    // base fee before any workspace opens), so don't wait for openWorkspace.
    if (this.hasKeyValue && this.confirmedValue) this.loadCosts()
  }

  // Wake-up knock for the API. The gateway Lambda sleeps, and a cold first
  // query is noticeably slower. /health is unauthenticated and free; the
  // cooldown caps this at one request a minute no matter how often it fires.
  // Warming one execution environment does not guarantee the real query
  // lands on it — this raises the odds, nothing more.
  warmUp() {
    if (!this.apiBaseUrlValue) return

    const now = Date.now()
    if (warmedAt !== null && now - warmedAt < WARM_UP_COOLDOWN_MS) return
    warmedAt = now

    let healthUrl
    try {
      healthUrl = `${new URL(this.apiBaseUrlValue).origin}/health`
    } catch (_e) {
      return
    }

    fetch(healthUrl, { mode: "no-cors", cache: "no-store" }).catch(() => {})
  }

  // Gate — defense-in-depth for users who bypass the `inert` wrapper
  // (e.g. via DevTools). Server also rejects these actions with 403.
  ensureConfirmed() {
    if (this.confirmedValue) return true
    const banner = document.getElementById("sandbox-confirm-banner")
    if (banner) {
      banner.scrollIntoView({ behavior: "smooth", block: "center" })
      banner.animate(
        [{ transform: "scale(1)" }, { transform: "scale(1.02)" }, { transform: "scale(1)" }],
        { duration: 350, iterations: 2 }
      )
    }
    return false
  }

  // Gate — if no API key is attached, dispatch an event that the key
  // panel's modal listens for (opens itself).
  ensureKey() {
    if (this.hasKeyValue) return true
    this.dispatch("need-key")
    return false
  }

  // ── Query detection ──
  //
  // pa_own_<n>            → direct entity lookup
  // digits and dashes     → resolve {ruc} (RUC, cédula — also passport w/ prefix)
  // anything else         → resolve {name}
  detect(raw) {
    const q = raw.trim()
    if (/^pa_own_\d+$/.test(q)) return { kind: "id", value: q }
    if (this.identifierLike(q)) return { kind: "ruc", value: q }
    return { kind: "nombre", value: q }
  }

  // RUCs and cédulas can carry letter segments anywhere, not just as a
  // prefix: 8-NT-1-12345 (RUC NT), 1AV-521-1638 / 8PI-… (cédulas AV/PI),
  // PE-123456, E-8-102345. Heuristic: dash-separated short alphanumeric
  // segments with at least one all-digit segment — a bare number also
  // counts, a single mixed token ("towerbank", "super99") does not.
  identifierLike(q) {
    if (q.includes(" ")) return false
    const segments = q.split("-")
    return segments.every((s) => /^[a-z0-9]{1,7}$/i.test(s)) &&
      segments.some((s) => /^\d+$/.test(s)) &&
      (segments.length >= 2 || /^\d+$/.test(q))
  }

  previewRoute() {
    if (!this.hasRoutePreviewTarget) return
    const q = this.queryTarget.value.trim()
    if (!q) {
      this.routePreviewTarget.textContent = 'POST /v5/resolve {"name": "…"}'
      return
    }
    const { kind, value } = this.detect(q)
    this.routePreviewTarget.textContent = kind === "id"
      ? `GET /v5/entities/${value}`
      : `POST /v5/resolve ${JSON.stringify(this.resolveBody(kind, value))}`
  }

  // The body the proxy sends upstream — same keys, so the ledger's cURL
  // repeats exactly the call that ran.
  resolveBody(kind, value) {
    return kind === "ruc" ? { ruc: value, top: 10 } : { name: value, top: 10 }
  }

  applyExample(event) {
    this.queryTarget.value = event.params.value
    this.previewRoute()
    this.queryTarget.focus()
  }

  maybeRun(event) {
    if (event.key === "Enter" && !event.repeat) this.run()
  }

  // ── Run: search or direct lookup ──

  async run() {
    // The disabled run button only gates clicks; Enter (and its key-repeat)
    // lands here directly, and every run is a billed upstream call.
    if (this.inFlight) return
    if (!this.ensureConfirmed() || !this.ensureKey()) return
    const raw = this.queryTarget.value.trim()
    if (!raw) return

    const { kind, value } = this.detect(raw)
    if (kind === "id") return this.lookupEntity(value)
    return this.search(kind, value)
  }

  async search(param, value) {
    this.closeWorkspace()
    this.resultsTarget.innerHTML = this.loadingHtml("Buscando…")
    this.setRunning(true)

    const proxyUrl = `${this.searchUrlValue}?${param}=${encodeURIComponent(value)}`
    const call = { method: "POST", path: "/resolve", body: this.resolveBody(param, value) }

    try {
      const { data, status, ms, bytes, serverCost } = await this.fetchJson(proxyUrl)
      this.logCall({ ...call, status, ms, bytes, cost: serverCost ?? this.costFor("_base") })
      this.renderResults(data.candidates || [])
    } catch (error) {
      this.logError(call, error)
      this.resultsTarget.innerHTML = this.errorHtml(error)
    } finally {
      this.setRunning(false)
    }
  }

  async lookupEntity(id) {
    this.resultsTarget.innerHTML = ""
    this.closeWorkspace()
    this.resultsTarget.innerHTML = this.loadingHtml("Consultando entidad…")
    this.setRunning(true)

    const proxyUrl = this.entityUrlTemplateValue.replace("__ID__", id)
    const call = { method: "GET", path: `/entities/${id}` }

    try {
      const { data, status, ms, bytes, serverCost } = await this.fetchJson(proxyUrl)
      this.logCall({ ...call, status, ms, bytes, cost: serverCost ?? this.costFor("_entity") })
      this.resultsTarget.innerHTML = ""
      this.openWorkspace({
        id: data.id || id,
        nombre: data.nombre,
        // A zero-balance key gets the DAT-FREE card, which says `tipo`.
        type: data.type || data.tipo,
        ruc: data.ruc_no_dv
      })
      // The summary call was billed — show its full payload, don't discard it.
      this.responseTarget.innerHTML =
        this.datFreeNoteHtml(data) + this.jsonPanelHtml(`/entities/${data.id || id}`, data)
    } catch (error) {
      this.logError(call, error)
      this.resultsTarget.innerHTML = this.errorHtml(error)
    } finally {
      this.setRunning(false)
    }
  }

  // POST /v5/resolve answers ranked candidates; the row shows the summary
  // plus how sure the match is. `disambiguator` is the candidate's RUC or
  // cédula — masked by the gateway on a zero-balance key.
  renderResults(candidates) {
    const results = candidates.map((c) => ({
      id: c.gid,
      nombre: c.summary?.nombre,
      type: c.summary?.type,
      ruc_no_dv: c.disambiguator ?? c.summary?.ruc_no_dv,
      confidence: c.confidence,
      matchedOn: (c.matched_on || []).join(", ")
    }))
    if (!results.length) {
      this.resultsTarget.innerHTML =
        `<p class="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">Sin resultados. Prueba otro nombre o identificador.</p>`
      return
    }

    this.resultsTarget.innerHTML = `
      <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        ${results.map((r) => `
          <button type="button"
                  class="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-2.5 text-left last:border-b-0 hover:bg-panadata-primary-light/30"
                  data-action="click->sandbox#selectResult"
                  data-sandbox-id-param="${this.escAttr(r.id)}"
                  data-sandbox-nombre-param="${this.escAttr(r.nombre || "")}"
                  data-sandbox-type-param="${this.escAttr(r.type || "")}"
                  data-sandbox-ruc-param="${this.escAttr(r.ruc_no_dv || "")}">
            <span class="w-20 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-center text-[11px] text-gray-500">${this.esc((r.type || "entidad").toLowerCase())}</span>
            <span class="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">${this.esc(r.nombre || "Sin nombre")}</span>
            ${r.ruc_no_dv ? `<span class="hidden shrink-0 text-xs font-mono text-gray-400 sm:inline">${this.esc(r.ruc_no_dv)}</span>` : ""}
            ${r.confidence != null ? `<span class="hidden shrink-0 text-xs font-mono text-gray-400 md:inline" title="matched_on: ${this.escAttr(r.matchedOn)}">${this.esc(Number(r.confidence).toFixed(2))}</span>` : ""}
            <span class="shrink-0 text-xs font-mono text-gray-400">${this.esc(r.id)}</span>
          </button>
        `).join("")}
      </div>`
  }

  selectResult(event) {
    this.openWorkspace({
      id: event.params.id,
      nombre: event.params.nombre,
      type: event.params.type,
      ruc: event.params.ruc
    })
  }

  // ── Entity workspace ──

  openWorkspace(entity) {
    this.entity = entity
    this.surfaceOffsets = {}
    this.responseTarget.innerHTML = ""
    this.entityHeaderTarget.innerHTML = `
      <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p class="text-base font-semibold text-gray-900">${this.esc(entity.nombre || entity.id)}</p>
        ${entity.type ? `<span class="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">${this.esc(entity.type)}</span>` : ""}
        ${entity.ruc ? `<span class="text-xs font-mono text-gray-400">RUC ${this.esc(entity.ruc)}</span>` : ""}
        <button type="button"
                class="rounded border border-gray-200 px-1.5 py-0.5 text-xs font-mono text-gray-500 hover:border-panadata-primary hover:text-panadata-primary"
                data-action="click->sandbox#copyText" data-copy-text="${this.escAttr(entity.id)}">${this.esc(entity.id)} ⧉</button>
      </div>`
    this.workspaceTarget.classList.remove("hidden")
    this.workspaceTarget.scrollIntoView({ behavior: "smooth", block: "start" })
    this.loadCosts()
  }

  closeWorkspace() {
    this.entity = null
    this.workspaceTarget.classList.add("hidden")
    this.entityHeaderTarget.innerHTML = ""
    this.responseTarget.innerHTML = ""
  }

  // ── Surface calls ──

  async runSurface(event) {
    if (!this.ensureConfirmed() || !this.ensureKey()) return
    if (!this.entity) return
    const surface = event.params.surface
    await this.fetchSurfacePage(surface, 0)
  }

  async loadMore(event) {
    if (!this.ensureConfirmed() || !this.ensureKey()) return
    const surface = event.params.surface
    const offset = (this.surfaceOffsets[surface] ?? 0) + PAGE_LIMIT
    await this.fetchSurfacePage(surface, offset)
  }

  // «Consultar de nuevo»: the user chooses to ask again, and that call bills.
  async refetchPage(event) {
    if (!this.ensureConfirmed() || !this.ensureKey() || !this.entity) return
    const { surface, offset } = event.params
    await this.fetchSurfacePage(surface, offset, { refetch: true })
  }

  async fetchSurfacePage(surface, offset, { refetch = false } = {}) {
    // One billable call at a time: a double-click on "Siguiente página" (its
    // offset only advances on response) or a chip clicked mid-flight would
    // otherwise bill the same page twice and race writes to responseTarget.
    if (this.inFlight) return
    // A page already answered in this session is shown again from memory: a
    // second click on the same chip billed the same page twice, empty or not.
    const pagePrefix = `${this.entity.id}|${surface}|`
    const pageKey = `${pagePrefix}${offset}`
    const loaded = refetch ? null : this.loadedPages.get(pageKey)
    if (loaded) {
      this.surfaceOffsets[surface] = offset
      this.renderSurface(surface, loaded, { offset, fromSession: true })
      return
    }
    this.inFlight = true
    const chip = this.element.querySelector(`[data-sandbox-surface-chip="${surface}"]`)
    if (chip) chip.disabled = true
    this.responseTarget.innerHTML = this.loadingHtml(`GET /${surface}…`)

    const proxyUrl = `${this.surfaceUrlTemplateValue
      .replace("__ID__", this.entity.id)
      .replace("__SURFACE__", surface)}?limit=${PAGE_LIMIT}&offset=${offset}`
    const call = { method: "GET", path: `/entities/${this.entity.id}/${surface}?limit=${PAGE_LIMIT}&offset=${offset}` }

    try {
      const { data, status, ms, bytes, serverCost } = await this.fetchJson(proxyUrl)
      this.surfaceOffsets[surface] = offset
      // Only a fresh answer replaces memory, so a refetch that fails keeps the
      // page already paid for. The surface's other pages are from the earlier
      // pull: a fresh page 1 next to a remembered page 2 would mix snapshots.
      if (refetch) {
        for (const key of this.loadedPages.keys()) {
          if (key.startsWith(pagePrefix)) this.loadedPages.delete(key)
        }
      }
      this.loadedPages.set(pageKey, data)
      this.logCall({ ...call, status, ms, bytes, cost: serverCost ?? this.costFor(surface) })
      this.renderSurface(surface, data, { status, ms, bytes, offset })
    } catch (error) {
      this.logError(call, error)
      // Several surfaces bill under one code (legal and css-morosos are both
      // DAT-RISK), so a lock names the chip it answers, not only the code.
      const surfaceLabel = chip?.dataset.sandboxSurfaceLabel
      this.responseTarget.innerHTML = `<div class="p-5">${this.errorHtml(error, { surfaceLabel })}</div>`
    } finally {
      this.inFlight = false
      if (chip) chip.disabled = false
    }
  }

  renderSurface(surface, data, meta) {
    // Not every surface is a list: screening, licenses, network and
    // trade-profile answer object envelopes with no top-level items. For
    // those the JSON view is the truth — never claim "en cero" from a
    // missing key (llave ausente ≠ array vacío).
    const hasItems = Array.isArray(data.items)
    const items = hasItems ? data.items : []
    const pagination = data.pagination || {}
    const page = Math.floor((meta.offset || 0) / PAGE_LIMIT) + 1
    const json = JSON.stringify(data, null, 2)
    const summary = hasItems
      ? `${items.length} items · página ${page}${pagination.has_more ? " · hay más" : ""}`
      : "objeto"
    const activeButton = "rounded-md border border-panadata-primary bg-panadata-primary-light px-2.5 py-1 text-xs font-mono text-panadata-primary-dark"
    const idleButton = "rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-mono text-gray-500"

    this.responseTarget.innerHTML = `
      <div class="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-gray-100 bg-gray-50 px-5 py-3">
        <code class="text-xs font-mono text-gray-600">/${this.esc(surface)}</code>
        <span class="text-xs font-mono text-gray-400">${this.esc(summary)}</span>
        ${meta.fromSession ? `<span class="text-xs text-gray-500" data-sandbox-from-session>Ya la consultaste en esta sesión: se muestra de nuevo sin otra llamada ni cargo.</span>
          <button type="button" class="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-panadata-primary hover:text-panadata-primary"
                  data-action="click->sandbox#refetchPage"
                  data-sandbox-surface-param="${this.escAttr(surface)}"
                  data-sandbox-offset-param="${meta.offset || 0}">Consultar de nuevo</button>
          <span class="text-xs text-gray-500" data-sandbox-refetch-note>${this.esc(this.refetchNote(surface))}</span>` : ""}
        <span class="flex-1"></span>
        <div class="flex gap-1" data-sandbox-view-buttons>
          <button type="button" class="${hasItems ? activeButton : idleButton}"
                  data-action="click->sandbox#toggleView" data-view="table" data-sandbox-view-button>Tabla</button>
          <button type="button" class="${hasItems ? idleButton : activeButton}"
                  data-action="click->sandbox#toggleView" data-view="json" data-sandbox-view-button>JSON</button>
        </div>
      </div>
      <div class="${hasItems ? "" : "hidden "}p-5" data-sandbox-view="table">
        ${hasItems
          ? this.tableHtml(items)
          : `<p class="text-sm text-gray-500">Esta superficie responde un objeto, no una lista — el detalle está en la vista JSON.</p>`}
        ${pagination.has_more ? `
          <button type="button" class="mt-3 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-panadata-primary hover:text-panadata-primary"
                  data-action="click->sandbox#loadMore" data-sandbox-surface-param="${this.escAttr(surface)}">
            Siguiente página (offset=${(meta.offset || 0) + PAGE_LIMIT})
          </button>` : ""}
      </div>
      <div class="${hasItems ? "hidden " : ""}bg-panadata-charcoal" data-sandbox-view="json">
        <div class="flex items-center justify-end border-b border-gray-700/60 px-5 py-2">
          <button type="button" class="rounded-md border border-gray-600 px-2.5 py-1 text-xs font-mono text-gray-300 hover:border-gray-400"
                  data-action="click->sandbox#copyText" data-copy-text="${this.escAttr(json)}">Copiar JSON</button>
        </div>
        <pre class="max-h-96 overflow-auto p-5 text-xs font-mono leading-6 text-green-300">${this.esc(json)}</pre>
      </div>`
  }

  // Said next to «Consultar de nuevo», not in a tooltip a phone never shows,
  // and in the ledger's terms: a zero-balance key is never charged.
  refetchNote(surface) {
    if (this.freeTierValue) return "Sin cargo: cuenta otra vez contra tu cuota gratuita."
    const cost = this.costFor(surface)
    return cost != null ? `Se cobra otra vez (~$${cost.toFixed(2)}).` : "Se cobra otra vez."
  }

  // The DAT-FREE card answers "is there something?", not "what is it" — say
  // so next to it, including the two things it never carries (a sanctions
  // flag and per-source counts), so its booleans are not read as the detail.
  datFreeNoteHtml(data) {
    if (data?.nivel !== "DAT-FREE") return ""
    return `
      <div class="border-b border-gray-100 px-5 py-3 text-sm leading-6 text-gray-600">
        Ficha <code class="font-mono text-xs">DAT-FREE</code>: señales sí/no y rangos, sin registros.
        <code class="font-mono text-xs">codigos_con_datos</code> dice qué códigos traen datos para esta entidad.
        No incluye screening de sanciones (<code class="font-mono text-xs">DAT-SCREEN</code>) ni conteos por fuente
        (<code class="font-mono text-xs">presencia.counters</code>, en <code class="font-mono text-xs">DAT-CORE</code>): esos requieren saldo.
      </div>`
  }

  // JSON-only panel for calls whose payload has no tabular shape (the
  // entity summary). Same chrome as the surface JSON view.
  jsonPanelHtml(path, data) {
    const json = JSON.stringify(data, null, 2)
    return `
      <div class="flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-5 py-3">
        <code class="text-xs font-mono text-gray-600">${this.esc(path)}</code>
      </div>
      <div class="bg-panadata-charcoal">
        <div class="flex items-center justify-end border-b border-gray-700/60 px-5 py-2">
          <button type="button" class="rounded-md border border-gray-600 px-2.5 py-1 text-xs font-mono text-gray-300 hover:border-gray-400"
                  data-action="click->sandbox#copyText" data-copy-text="${this.escAttr(json)}">Copiar JSON</button>
        </div>
        <pre class="max-h-96 overflow-auto p-5 text-xs font-mono leading-6 text-green-300">${this.esc(json)}</pre>
      </div>`
  }

  // Generic table: columns are the first item's scalar fields (up to 5).
  // Anything deeper lives one click away in the JSON view.
  tableHtml(items) {
    if (!items.length) {
      return `<p class="text-sm text-gray-500">Consultado y en cero: esta entidad no tiene registros en esta superficie.</p>`
    }

    const columns = Object.entries(items[0])
      .filter(([, v]) => v === null || ["string", "number", "boolean"].includes(typeof v))
      .slice(0, 5)
      .map(([k]) => k)

    if (!columns.length) return `<p class="text-sm text-gray-500">Items sin campos planos — usa la vista JSON.</p>`

    return `
      <div class="overflow-x-auto">
        <table class="w-full text-left text-sm">
          <thead>
            <tr class="border-b border-gray-200">
              ${columns.map((c) => `<th class="py-2 pr-4 text-xs font-mono font-normal text-gray-400">${this.esc(c)}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${items.map((item) => `
              <tr class="border-b border-gray-100 last:border-b-0">
                ${columns.map((c) => `<td class="max-w-64 truncate py-2 pr-4 text-gray-700">${this.esc(this.cell(item[c]))}</td>`).join("")}
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>`
  }

  cell(value) {
    if (value === null || value === undefined || value === "") return "—"
    return String(value)
  }

  toggleView(event) {
    const view = event.currentTarget.dataset.view
    this.responseTarget.querySelectorAll("[data-sandbox-view]").forEach((el) => {
      el.classList.toggle("hidden", el.dataset.sandboxView !== view)
    })
    this.responseTarget.querySelectorAll("[data-sandbox-view-button]").forEach((btn) => {
      const active = btn.dataset.view === view
      btn.classList.toggle("border-panadata-primary", active)
      btn.classList.toggle("bg-panadata-primary-light", active)
      btn.classList.toggle("text-panadata-primary-dark", active)
      btn.classList.toggle("border-gray-200", !active)
      btn.classList.toggle("bg-white", !active)
      btn.classList.toggle("text-gray-500", !active)
    })
  }

  // ── Costs (free dry-run quotes; global, cached server-side) ──

  loadCosts() {
    if (this.costs || this.costsLoading) return
    this.costsLoading = true
    fetch(this.costsUrlValue, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((costs) => {
        this.costs = costs
        if (!costs) return
        this.element.querySelectorAll("[data-sandbox-cost-for]").forEach((el) => {
          const quote = costs[el.dataset.sandboxCostFor]
          if (quote?.credits != null) el.textContent = `$${Number(quote.credits).toFixed(2)}`
        })
      })
      .catch(() => {})
      // Any failure (network throw or a non-OK response, e.g. a 502 while
      // the server cache warms) must leave the door open for a retry on the
      // next workspace open — never latch a transient error for the session.
      .finally(() => { this.costsLoading = false })
  }

  costFor(surface) {
    const quote = this.costs?.[surface]
    return quote?.credits != null ? Number(quote.credits) : null
  }

  // ── Request ledger ──

  logCall({ method = "GET", path, body, status, ms, bytes, cost }) {
    const ok = status >= 200 && status < 300
    // A zero-balance key is never charged: the call counts against the free
    // allowance, so the quote is what it would cost with credits, not spend.
    if (cost != null && !this.freeTierValue) {
      this.sessionCost += cost
      if (this.hasLedgerTotalTarget) this.ledgerTotalTarget.textContent = `~$${this.sessionCost.toFixed(2)}`
    }
    this.appendLedgerRow(`
      <li class="flex items-baseline gap-3 whitespace-nowrap">
        <span class="text-gray-500">${this.esc(method)}</span>
        <span class="min-w-0 flex-1 truncate text-gray-300" title="${this.escAttr(this.callLabel(path, body))}">${this.esc(this.callLabel(path, body))}</span>
        <span class="${ok ? "text-green-400" : "text-red-400"}">${status}</span>
        <span class="text-gray-500">${this.esc(this.formatMs(ms))}</span>
        ${bytes != null ? `<span class="text-gray-500">${this.formatBytes(bytes)}</span>` : ""}
        ${this.ledgerCostHtml(cost)}
        <button type="button" class="rounded border border-gray-600 px-1.5 text-[11px] text-gray-400 hover:border-gray-400 hover:text-gray-200"
                data-action="click->sandbox#copyText" data-copy-text="${this.escAttr(this.curlFor(path, body))}">curl</button>
      </li>`)
  }

  ledgerCostHtml(cost) {
    if (this.freeTierValue) {
      const title = cost != null
        ? `Sin cargo: cuenta contra tu cuota gratuita. Con saldo costaría ~$${cost.toFixed(2)}.`
        : "Sin cargo: cuenta contra tu cuota gratuita."
      return `<span class="text-gray-300" data-sandbox-ledger-cost="free" title="${this.escAttr(title)}">gratis</span>`
    }
    return `<span class="text-gray-400">${cost != null ? `~$${cost.toFixed(2)}` : "$—"}</span>`
  }

  logError({ method = "GET", path, body }, error) {
    const status = error.upstreamStatus ?? error.status ?? "—"
    this.appendLedgerRow(`
      <li class="flex items-baseline gap-3 whitespace-nowrap">
        <span class="text-gray-500">${this.esc(method)}</span>
        <span class="min-w-0 flex-1 truncate text-gray-300" title="${this.escAttr(this.callLabel(path, body))}">${this.esc(this.callLabel(path, body))}</span>
        <span class="text-red-400">${this.esc(String(status))}</span>
        <span class="min-w-0 truncate text-gray-500">${this.esc(error.message)}</span>
      </li>`)
  }

  appendLedgerRow(html) {
    this.ledgerTarget.classList.remove("hidden")
    this.ledgerListTarget.insertAdjacentHTML("beforeend", html)
    this.ledgerRows += 1
    this.ledgerCountTarget.textContent = this.ledgerRows
    while (this.ledgerListTarget.children.length > LEDGER_MAX_ROWS) {
      this.ledgerListTarget.firstElementChild.remove()
    }
    this.ledgerListTarget.lastElementChild?.scrollIntoView({ block: "nearest" })
  }

  callLabel(path, body) {
    return body ? `${path} ${JSON.stringify(body)}` : path
  }

  curlFor(path, body) {
    const auth = `curl "${this.apiBaseUrlValue}${path}" \\\n  --header "Authorization: Bearer $PANADATA_API_KEY"`
    if (!body) return auth
    // `--data` makes it a POST. Single-quoted for the shell; a quote inside a
    // name is closed, escaped and reopened.
    const json = JSON.stringify(body).replaceAll("'", "'\\''")
    return `${auth} \\\n  --header "Content-Type: application/json" \\\n  --data '${json}'`
  }

  // ── Plumbing ──

  async fetchJson(url) {
    const t0 = performance.now()
    const response = await fetch(url, { headers: { Accept: "application/json" } })
    const text = await response.text()
    const ms = Math.round(performance.now() - t0)
    let payload = {}
    try { payload = JSON.parse(text) } catch (_e) { payload = {} }
    if (!response.ok) {
      const error = new Error(payload.error || `La solicitud falló con status ${response.status}.`)
      error.status = response.status
      // The sandbox answers 502 for every upstream failure, so the real
      // status travels in the body. Absent means no response ever arrived.
      error.upstreamStatus = payload.upstream_status ?? null
      error.upstreamCode = payload.upstream_code ?? null
      error.lockedCodes = payload.locked_codes ?? []
      error.action = payload.action ?? null
      error.upstreamDetail = payload.upstream_detail ?? null
      throw error
    }
    // The proxy stamps each response with its cost, resolved server-side —
    // the authoritative figure the ledger prefers over the client quote map.
    const costHeader = parseFloat(response.headers.get("X-Sandbox-Cost"))
    return {
      data: payload,
      status: response.status,
      ms,
      bytes: text.length,
      serverCost: Number.isFinite(costHeader) ? costHeader : null
    }
  }

  setRunning(running) {
    this.inFlight = running
    this.runButtonTarget.disabled = running
    this.runButtonTarget.textContent = running ? "Ejecutando…" : "Ejecutar"
  }

  copyText(event) {
    const text = event.currentTarget.dataset.copyText
    if (!text) return

    navigator.clipboard.writeText(text).then(() => {
      const el = event.currentTarget
      const original = el.textContent
      el.textContent = "Copiado"
      setTimeout(() => { el.textContent = original }, 1200)
    })
  }

  loadingHtml(message) {
    return `
      <div class="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
        <span class="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-panadata-primary"></span>
        <span class="text-sm font-mono text-gray-600">${this.esc(message)}</span>
      </div>`
  }

  // The cold-start hint only when the failure could actually be a cold
  // start; otherwise it tells someone whose entity does not exist, or who
  // ran out of credits, to keep retrying forever.
  errorHtml(error, { surfaceLabel } = {}) {
    const hint = this.coldStartLikely(error)
      ? `<p class="mt-2 text-xs text-red-600">La primera consulta puede tardar unos segundos si el servicio está en cold-start. Vuelve a ejecutarla.</p>`
      : ""

    if (error?.upstreamCode === "free_tier_code_locked") return this.freeTierLockedHtml(error, surfaceLabel)

    // A refusal about money or the key is not a broken call: amber, with the
    // page that fixes it. Everything else stays red.
    const refusal = BILLING_REFUSAL_CODES.includes(error?.upstreamCode)
    const tone = refusal
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : "border-red-200 bg-red-50 text-red-700"
    const action = error?.action?.href
      ? ` <a href="${this.escAttr(error.action.href)}" class="font-medium text-panadata-primary hover:underline">${this.esc(error.action.label)}</a>.`
      : ""
    // The gateway's own words, for whoever wants the raw answer.
    const detail = error?.upstreamDetail && error.upstreamDetail !== error.message
      ? `<p class="mt-1 font-mono text-xs opacity-70">API: ${this.esc(error.upstreamDetail)}</p>`
      : ""

    return `<div class="rounded-lg border ${tone} px-4 py-3 text-sm leading-6"><p>${this.esc(error.message)}${action}</p>${detail}${hint}</div>`
  }

  // Not a failure of the call: the key is on the free tier and asked for
  // records. Say which surface and codes, what the key can still do, and
  // where to top up. Without a surface (search, the entity card) the call
  // itself is the subject.
  freeTierLockedHtml(error, surfaceLabel) {
    const codes = (error.lockedCodes || []).join(", ")
    const codesHtml = `<code class="font-mono text-xs">${this.esc(codes)}</code>`
    const headline = surfaceLabel
      ? `${this.esc(surfaceLabel)} requiere saldo${codes ? ` (${codesHtml})` : ""}.`
      : `Esta consulta requiere saldo${codes ? `: ${codesHtml}` : ""}.`
    const pricing = this.pricingUrlValue
      ? ` <a href="${this.escAttr(this.pricingUrlValue)}" class="font-medium text-panadata-primary hover:underline">Ver precios</a>.`
      : ""
    return `
      <div class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
        <p class="font-semibold">${headline}</p>
        <p class="mt-1">Con saldo cero tu llave responde la búsqueda y la ficha <code class="font-mono text-xs">DAT-FREE</code>, no los registros.${pricing}</p>
      </div>`
  }

  // 502 alone means nothing: the sandbox maps every upstream failure to it,
  // 404 and 429 included. `upstreamStatus === null` is the real signal —
  // no response arrived before the proxy's read timeout.
  coldStartLikely(error) {
    if (!error || error.status !== 502) return false
    if (error.upstreamStatus === null || error.upstreamStatus === undefined) return true
    return COLD_START_UPSTREAM_STATUSES.includes(error.upstreamStatus)
  }

  formatMs(ms) {
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`
  }

  formatBytes(length) {
    return length < 1024 ? `${length} B` : `${(length / 1024).toFixed(1)} KB`
  }

  esc(value) {
    if (value === null || value === undefined) return ""
    return String(value).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll("\"", "&quot;")
  }

  escAttr(value) {
    return this.esc(value).replaceAll("'", "&#39;")
  }
}
